import { useRef, useState } from 'react';
import { DeferredChoiceMenu as ChoiceMenu } from '~/ui/deferred/deferred-menus';
import { FormattedMessage, useIntl } from 'react-intl';
import { FilterTrigger } from './filter-trigger';
import { pick } from './pick';

export type LanguageOption = {
  id: string;
  label: string;
};

type LanguageFilterProps = {
  value: string[];
  options: LanguageOption[];
  onChange: (value: string[]) => void;
  /** Fired when the menu opens, for analytics. */
  onOpen?: () => void;
};

/**
 * Multi-select language filter for the home page search bar. Renders the
 * shared `<FilterTrigger>` (no leading icon) and anchors a Leaf
 * `ChoiceMenu`. The trigger's value shows the count of selected languages,
 * or "All" when nothing is chosen, so the bar stays scannable when
 * collapsed.
 *
 * Selections are committed via `onChange` on EVERY toggle (not on menu close)
 * — the route reflects the change instantly: cards re-filter client-side and
 * the upcoming-events carousel refetches (US-10172). `value` is the committed
 * source of truth (the URL), so the listbox reads straight from it.
 */
export function LanguageFilter({
  value,
  options,
  onChange,
  onOpen,
}: LanguageFilterProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const intl = useIntl();

  const selected = pick(options, value);

  return (
    <>
      <FilterTrigger
        ref={triggerRef}
        label={
          <FormattedMessage
            defaultMessage="Language"
            description="Label on the language filter button in the home page search bar"
          />
        }
        value={
          selected.length === 0 || selected.length === options.length ? (
            <FormattedMessage
              defaultMessage="All"
              description="Placeholder on the language filter button when no languages are selected"
            />
          ) : selected.length === 1 ? (
            capitalizeFirst(selected[0]!.label, intl.locale)
          ) : (
            <FormattedMessage
              defaultMessage="{count} languages"
              description="Summary on the language filter button when multiple languages are selected"
              values={{ count: selected.length }}
            />
          )
        }
        open={open}
        popupKind="listbox"
        onClick={() => {
          setOpen((wasOpen) => {
            if (!wasOpen) onOpen?.();
            return !wasOpen;
          });
        }}
      />

      <ChoiceMenu
        anchor={triggerRef}
        open={open}
        onOpenChange={setOpen}
        placement="bottom start"
        multiple
        value={value}
        onChange={onChange}
        items={options.map((o) => ({
          value: o.id,
          label: capitalizeFirst(o.label, intl.locale),
        }))}
        aria-label={intl.formatMessage({
          defaultMessage: 'Select languages',
          description: 'Aria label for the home page language filter listbox',
        })}
      />
    </>
  );
}

/**
 * Capitalize the first letter of a language display name. `Intl.DisplayNames`
 * returns lowercase names in some locales (e.g. Danish "engelsk"), so we
 * capitalize at render for a consistent, title-like menu. Locale-aware upper-
 * casing (via the UI locale) handles locale-specific letter rules.
 */
function capitalizeFirst(label: string, locale: string): string {
  if (!label) return label;
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}
